import React, { useEffect, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { Icon } from '@components/shared/Icon';
import { Button, ButtonAppearance } from '@components/shared/Button';
import { useSlideMenuContext } from '../context/NavbarContext';

interface SlideMenuProps {
  navRef: React.RefObject<HTMLDivElement>;
}

export const SlideMenu: React.FC<SlideMenuProps> = ({ navRef }) => {
  const navWrapperTopPosition = useMemo(() => navRef.current?.getBoundingClientRect()?.y || 56, [navRef]);
  const ref = useRef<HTMLDivElement>(null);
  const { closeSlideMenu, slideMenuContent, isSlideMenuOpen, setContainerHeight } = useSlideMenuContext();
  const { header, content } = slideMenuContent || {};

  const isTitleString = typeof header === 'string';

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const preventScroll = (e: WheelEvent | TouchEvent): void => {
      if (element.contains(e.target as Node)) {
        return;
      }
      e.preventDefault();
    };

    element.addEventListener('wheel', preventScroll);
    element.addEventListener('touchmove', preventScroll);

    return () => {
      element.removeEventListener('wheel', preventScroll);
      element.removeEventListener('touchmove', preventScroll);
    };
  }, []);

  useEffect(() => {
    if (isSlideMenuOpen) {
      setContainerHeight(ref.current?.getBoundingClientRect().height || 'initial');
    }
  }, [isSlideMenuOpen]);

  return (
    <div
      ref={ref}
      data-testid="slide-menu"
      css={[
        menuStyles,
        css({
          maxHeight: `calc(100vh - ${navWrapperTopPosition}px)`,
        }),
      ]}
      aria-hidden={!isSlideMenuOpen}
    >
      <div css={backButtonContainerStyles}>
        <Button
          appearance={ButtonAppearance.LINK}
          onClick={closeSlideMenu}
          iconBefore={<Icon type="chevron-left" size="medium" />}
          label="Back"
        />
      </div>
      {header && <div css={[!isTitleString && headerStyles, isTitleString && titleStyles]}>{header}</div>}
      {content && <div css={contentStyles}>{content}</div>}
    </div>
  );
};

const menuStyles = css({
  position: 'fixed',
  width: '375px',
  overflow: 'auto',
  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  zIndex: 1000,
  padding: token('space.300'),
  top: 0,

  [media.below.sm]: {
    transition: 'transform 0.3s cubic-bezier(.4,1,.5,1), opacity 0.3s',
    transform: 'translateX(-100%)',
    left: 0,
    width: '100vw',
  },
});

const backButtonContainerStyles = css({
  marginBottom: token('space.250'),

  '& button': {
    padding: 0,
    marginLeft: '-9px',
    fontWeight: 400,
  },
});

const headerStyles = css({
  textTransform: 'uppercase',
  marginLeft: token('space.100'),
  marginBottom: token('space.200'),

  '& > div': {
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: 600,
    fontFamily: 'Charlie Text, sans-serif',
  },
});

const titleStyles = css({
  fontSize: '12px',
  lineHeight: '16px',
  fontWeight: 600,
  fontFamily: 'Charlie Text, sans-serif',
  textTransform: 'uppercase',
  marginBottom: token('space.150'),
  marginTop: token('space.400'),
});

const contentStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.150'),
});
